import {
    computed,
    defineComponent,
    onMounted,
    ref,
    watch,
} from "vue";
import { DefaultEntityClient } from "../../core/api/EntityClient.js";
import type { LogChannel, LogUser } from "../../core/types.js";
import { useLocale } from "../../locales/i18n.js";
import {
    PermissionMode,
    PermissionSource,
    PermissionSimulationResponse,
    SimulationMember,
    SimulatedPermission,
    simulateDiscordPermissions,
} from "./api.js";

const Modes = [
    { key: "server", icon: "admin_panel_settings", label: "Permissions serveur" },
    { key: "channel", icon: "tag", label: "Permissions salons" },
    { key: "member", icon: "person_check", label: "Action sur membre" },
] as const satisfies readonly { key: PermissionMode; icon: string; label: string }[];

export default defineComponent({
    setup() {
        const { i18n } = useLocale();
        const mode = ref<PermissionMode>("server");
        const user = ref<LogUser | null>(null);
        const target = ref<LogUser | null>(null);
        const channelId = ref("");
        const channels = ref<LogChannel[]>([]);
        const channelSearch = ref("");
        const permissionSearch = ref("");
        const result = ref<PermissionSimulationResponse | null>(null);
        const loading = ref(false);
        const error = ref<string | null>(null);

        const filteredChannels = computed(() => {
            const query = channelSearch.value.trim().toLowerCase();

            return channels.value
                .filter((channel) => !query || getChannelName(channel).toLowerCase().includes(query) || channel.id.includes(query))
                .slice(0, 80);
        });
        const visiblePermissions = computed(() => {
            if (!result.value || result.value.mode === "member")
                return [];

            const query = permissionSearch.value.trim().toLowerCase();

            return result.value.permissions.filter((permission) => {
                const label = i18n(`permissions.${permission.key}.name`);

                return !query
                    || permission.key.toLowerCase().includes(query)
                    || label.toLowerCase().includes(query);
            });
        });

        onMounted(async () => {
            channels.value = await DefaultEntityClient.getChannels().catch(() => []);
        });

        watch([ mode, user, target, channelId ], () => {
            result.value = null;
            error.value = null;
        });

        async function runSimulation() {
            if (!user.value)
                return;

            if (mode.value === "channel" && !channelId.value) {
                error.value = "Sélectionne un salon ou une catégorie.";
                return;
            }

            if (mode.value === "member" && !target.value) {
                error.value = "Sélectionne le membre cible.";
                return;
            }

            loading.value = true;
            error.value = null;

            try {
                result.value = await simulateDiscordPermissions({
                    mode          : mode.value,
                    userId        : user.value.id,
                    channelId     : channelId.value || undefined,
                    targetUserId  : target.value?.id,
                });

            } catch (err) {
                error.value = err instanceof Error ? err.message : "Simulation impossible.";

            } finally {
                loading.value = false;
            }
        }

        return () => (
            <div class="permission-simulator-page">
                <div class="permission-simulator-hero">
                    <div>
                        <span class="permission-simulator-eyebrow">Discord permissions lab</span>
                        <h1>Simulateur de permissions</h1>
                        <p>Teste les permissions effectives Discord d’un membre, les dérogations salon et les actions possibles sur une cible.</p>
                    </div>
                    <button class="permission-simulator-run" disabled={!user.value || loading.value} onClick={runSimulation}>
                        <i class="material-symbols-outlined">{loading.value ? "progress_activity" : "play_arrow"}</i>
                        {loading.value ? "Simulation..." : "Simuler"}
                    </button>
                </div>

                <div class="permission-simulator-layout">
                    <div class="permission-simulator-panel">
                        <UserPicker
                            label="Utilisateur à tester"
                            modelValue={user.value}
                            onSelect={(selected) => user.value = selected}
                        />

                        <div class="permission-simulator-mode-list">
                            {Modes.map((entry) => (
                                <button
                                    key={entry.key}
                                    class={{ "permission-simulator-mode": true, active: mode.value === entry.key }}
                                    onClick={() => mode.value = entry.key}
                                >
                                    <i class="material-symbols-outlined">{entry.icon}</i>
                                    {entry.label}
                                </button>
                            ))}
                        </div>

                        {(mode.value === "channel" || mode.value === "member") && (
                            <ChannelPicker
                                channels={filteredChannels.value}
                                search={channelSearch.value}
                                modelValue={channelId.value}
                                optional={mode.value === "member"}
                                onSearch={(value) => channelSearch.value = value}
                                onSelect={(value) => channelId.value = value}
                            />
                        )}

                        {mode.value === "member" && (
                            <UserPicker
                                label="Membre cible"
                                modelValue={target.value}
                                onSelect={(selected) => target.value = selected}
                            />
                        )}
                    </div>

                    <div class="permission-simulator-results">
                        {error.value && <div class="permission-simulator-error">{error.value}</div>}

                        {!result.value && !error.value && (
                            <div class="permission-simulator-empty">
                                <i class="material-symbols-outlined">fact_check</i>
                                <strong>Configure une simulation</strong>
                                <span>Sélectionne un utilisateur, un mode, puis lance le calcul.</span>
                            </div>
                        )}

                        {result.value?.mode !== "member" && result.value && (
                            <PermissionResultList
                                i18n={i18n}
                                permissions={visiblePermissions.value}
                                search={permissionSearch.value}
                                onSearch={(value) => permissionSearch.value = value}
                            />
                        )}

                        {result.value?.mode === "member" && (
                            <ActionResult result={result.value} i18n={i18n} />
                        )}
                    </div>
                </div>
            </div>
        );
    },
});

const UserPicker = defineComponent<{
    label: string;
    modelValue: LogUser | null;
    onSelect: (user: LogUser) => void;
}>((props) => {
    const query = ref("");
    const options = ref<LogUser[]>([]);
    const loading = ref(false);
    let searchVersion = 0;

    watch(query, async (value) => {
        const trimmed = value.trim();
        const version = ++searchVersion;

        if (trimmed.length < 2) {
            options.value = [];
            loading.value = false;
            return;
        }

        loading.value = true;
        const users = await DefaultEntityClient.searchUsers(trimmed).catch(() => []);

        if (version !== searchVersion)
            return;

        options.value = users;
        loading.value = false;
    });

    function selectUser(selected: LogUser) {
        searchVersion++;
        props.onSelect(selected);
        query.value = "";
        options.value = [];
        loading.value = false;
    }

    return () => (
        <div class="permission-simulator-field">
            <label>{props.label}</label>
            <div class="permission-simulator-picker">
                <input
                    value={query.value}
                    placeholder={props.modelValue ? "Rechercher un autre membre" : "Nom, pseudo ou ID Discord"}
                    onInput={(event) => query.value = (event.target as HTMLInputElement).value}
                />
                {(loading.value || options.value.length > 0) && (
                    <div class="permission-simulator-options permission-simulator-options-floating">
                        {loading.value && <span class="permission-simulator-loading">Recherche...</span>}
                        {options.value.map((option) => (
                            <button key={option.id} onClick={() => selectUser(option)}>
                                <UserAvatar user={option} />
                                <span>{getUserName(option)}</span>
                                <small>{option.id}</small>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {props.modelValue && <SelectedUser user={props.modelValue} />}
        </div>
    );
}, { props: ["label", "modelValue", "onSelect"] });

const ChannelPicker = defineComponent<{
    channels: LogChannel[];
    search: string;
    modelValue: string;
    optional: boolean;
    onSearch: (value: string) => void;
    onSelect: (value: string) => void;
}>((props) => () => (
    <div class="permission-simulator-field">
        <label>{props.optional ? "Salon optionnel" : "Salon ou catégorie"}</label>
        <input
            value={props.search}
            placeholder="Rechercher un salon"
            onInput={(event) => props.onSearch((event.target as HTMLInputElement).value)}
        />
        {props.optional && (
            <button class="permission-simulator-clear" onClick={() => props.onSelect("")}>
                Aucun salon
            </button>
        )}
        <div class="permission-simulator-options compact">
            {props.channels.map((channel) => (
                <button
                    key={channel.id}
                    class={{ active: props.modelValue === channel.id }}
                    onClick={() => props.onSelect(channel.id)}
                >
                    <i class="material-symbols-outlined">{getChannelIcon(channel)}</i>
                    <span>{getChannelName(channel)}</span>
                </button>
            ))}
        </div>
    </div>
), { props: ["channels", "search", "modelValue", "optional", "onSearch", "onSelect"] });

function PermissionResultList(props: {
    i18n: (key: string, ...args: unknown[]) => string;
    permissions: SimulatedPermission[];
    search: string;
    onSearch: (value: string) => void;
}) {
    return (
        <>
            <div class="permission-simulator-results-header">
                <h2>Permissions effectives</h2>
                <input
                    value={props.search}
                    placeholder="Filtrer une permission"
                    onInput={(event) => props.onSearch((event.target as HTMLInputElement).value)}
                />
            </div>
            <div class="permission-simulator-permission-grid">
                {props.permissions.map((permission) => (
                    <div key={permission.key} class={{ "permission-simulator-permission": true, allowed: permission.allowed }}>
                        <div>
                            <i class="material-symbols-outlined">{permission.allowed ? "check_circle" : "block"}</i>
                            <strong>{props.i18n(`permissions.${permission.key}.name`)}</strong>
                            <small>{permission.key}</small>
                        </div>
                        <SourceList title="Autorisé par" sources={permission.allowedBy} />
                        <SourceList title="Interdit par" sources={permission.deniedBy} />
                    </div>
                ))}
            </div>
        </>
    );
}

function ActionResult(props: { result: Extract<PermissionSimulationResponse, { mode: "member" }>; i18n: (key: string) => string }) {
    return (
        <>
            <div class="permission-simulator-action-context">
                <SelectedUser user={props.result.user} label="Utilisateur" />
                <SelectedSimulationMember member={props.result.target} label="Cible" />
                {props.result.channel && <strong>Salon testé : #{props.result.channel.name}</strong>}
            </div>
            <div class="permission-simulator-actions">
                {props.result.actions.map((action) => (
                    <div key={action.key} class={{ "permission-simulator-action": true, allowed: action.allowed }}>
                        <i class="material-symbols-outlined">{action.allowed ? "check_circle" : "cancel"}</i>
                        <div>
                            <strong>{props.i18n(`permissions.${action.required[0]}.name`)}</strong>
                            {action.reasons.map((reason) => <span>{reason}</span>)}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}

function SourceList(props: { title: string; sources: PermissionSource[] }) {
    if (!props.sources.length)
        return null;

    return (
        <div class="permission-simulator-sources">
            <span>{props.title}</span>
            <div>
                {props.sources.map((source) => (
                    <span
                        class={{ "permission-simulator-source": true, "permission-simulator-source-effective": source.effective }}
                        key={`${source.type}:${source.id}:${source.viaPermission ?? ""}`}
                    >
                        <i style={{ backgroundColor: source.color ? `#${source.color.toString(16).padStart(6, "0")}` : undefined }} />
                        {source.name}
                        {source.effective && <small>effectif</small>}
                        {source.viaPermission && <small>via {source.viaPermission}</small>}
                    </span>
                ))}
            </div>
        </div>
    );
}

function SelectedUser(props: { user: LogUser | SimulationMember; label?: string }) {
    return (
        <div class="permission-simulator-selected-user">
            <UserAvatar user={props.user} />
            <div>
                {props.label && <small>{props.label}</small>}
                <strong>{getUserName(props.user)}</strong>
                <span>{props.user.id}</span>
            </div>
        </div>
    );
}

function SelectedSimulationMember(props: { member: SimulationMember; label: string }) {
    return <SelectedUser user={props.member} label={props.label} />;
}

function UserAvatar(props: { user: LogUser | SimulationMember }) {
    const avatar = getUserAvatar(props.user);

    return avatar
        ? <img src={avatar} alt="" />
        : <i class="material-symbols-outlined">person</i>;
}

function getUserName(user: LogUser | SimulationMember) {
    if (isSimulationMember(user))
        return user.displayName;

    return user.serverNickname
        ?? user.user?.globalName
        ?? user.user?.username
        ?? user.id;
}

function getUserAvatar(user: LogUser | SimulationMember) {
    if (isSimulationMember(user))
        return user.avatar;

    return user.serverAvatar as string | null | undefined
        ?? user.user?.avatar
        ?? null;
}

function isSimulationMember(user: LogUser | SimulationMember): user is SimulationMember {
    return "username" in user && "highestRole" in user;
}

function getChannelName(channel: LogChannel) {
    return channel.name ?? channel.id;
}

function getChannelIcon(channel: LogChannel) {
    if (channel.type === 4)
        return "folder";

    if (channel.type === 2 || channel.type === 13)
        return "settings_voice";

    return "tag";
}
